import { Component, Suspense } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

/**
 * A moldura das telas carregadas sob demanda.
 *
 * Desde que as rotas passaram a ser `lazy()`, entrar numa tela pode envolver
 * uma ida à rede — e duas coisas que não existiam antes passaram a ser
 * possíveis: a espera, e a falha.
 */

/**
 * O que aparece enquanto o pedaço da tela chega.
 *
 * ⚠️ **Aparece com atraso, de propósito** (`route-fallback` no index.css). Numa
 * conexão boa o chunk chega em poucas dezenas de milissegundos, e um
 * "Carregando…" que pisca nesse intervalo é ruído puro: a pessoa registra o
 * lampejo, não a palavra. Quem espera de verdade — que é justamente quem este
 * texto serve — passa do atraso e o lê.
 *
 * Quem pediu menos animação no sistema recebe o texto na hora: o bloco
 * `prefers-reduced-motion` do index.css zera o atraso junto com o resto.
 */
function RouteFallback() {
  const { t } = useTranslation();

  return (
    <p role="status" className="route-fallback text-sm text-chalk-500 dark:text-chalk-400">
      {t('cloud.loading')}
    </p>
  );
}

function RouteError() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-sm text-chalk-600 dark:text-chalk-400">{t('cloud.errorGeneric')}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex min-h-11 items-center rounded-lg border border-chalk-300 px-4 text-sm font-semibold hover:bg-chalk-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus dark:border-pitch-600 dark:hover:bg-pitch-800"
      >
        {t('common.retry')}
      </button>
    </div>
  );
}

/** Quando tentamos recarregar por chunk faltando, em ms desde a época. */
const RELOAD_KEY = 'balancedsquad.chunkReload';

/**
 * Janela para considerar que o recarregamento anterior foi por esta mesma
 * falha. Curta porque ela só precisa cobrir "recarregou e quebrou de novo na
 * mesma hora"; passado isso, uma falha nova merece a sua própria tentativa.
 */
const RELOAD_WINDOW_MS = 10_000;

/**
 * A falha é do PEDAÇO que não chegou, e não do código que ele contém.
 *
 * O sintoma clássico é o deploy: `index.html` já veio com os nomes novos e o
 * chunk antigo saiu do ar, ou o contrário — a aba aberta há uma hora pede um
 * arquivo que o deploy de agora renomeou. Nos dois casos o remédio é o mesmo, e
 * é buscar a página de novo.
 *
 * ⚠️ **Este projeto tem esse intervalo documentado**: depois do CI verde, a
 * versão nova leva ~20–30 s para propagar, e nesse meio-tempo o `index.html`
 * servido ainda aponta para o bundle anterior (ver "Armadilha do deploy" no
 * ESTADO.md). Enquanto tudo vinha num arquivo só, isso não quebrava navegação
 * nenhuma; com as rotas separadas, quem estivesse com o app aberto durante um
 * deploy bateria numa tela morta ao trocar de aba.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);

  // ⚠️ Cada alternativa precisa nomear o MÓDULO, e não só a falha. Um
  // `error loading` solto — que cobriria a frase do Firefox — casaria também
  // com um "Error loading user data" vindo de qualquer tela, e aí um defeito
  // de código viraria recarregamento: a pessoa veria a mesma tela quebrar de
  // novo e o defeito ficaria escondido atrás do que parece instabilidade. A
  // frase do Firefox ("error loading dynamically imported module") já entra
  // pela primeira alternativa.
  return /dynamically imported module|Importing a module script failed|Loading chunk/i.test(
    message,
  );
}

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < RELOAD_WINDOW_MS;
  } catch {
    // sessionStorage indisponível (modo privado em alguns navegadores). Sem o
    // marco não dá para saber se já tentamos, e recarregar às cegas arriscaria
    // um laço — então a resposta é "sim, já tentou", que cai na tela de erro.
    return true;
  }
}

interface State {
  failed: boolean;
}

/**
 * Recarrega UMA vez quando o pedaço da tela não chega; na segunda, mostra erro.
 *
 * O limite de uma tentativa é o que separa "o deploy trocou os arquivos debaixo
 * de você" de "este chunk está realmente quebrado". A primeira se resolve
 * sozinha e a pessoa nem percebe; a segunda, recarregada em laço, viraria uma
 * aba piscando para sempre — que é pior que a tela de erro, porque nem o botão
 * de tentar de novo ela oferece.
 */
class RouteBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    // Erro de render do componente não é chunk faltando: recarregar não
    // conserta, e ainda esconderia o defeito atrás de uma tela que volta igual.
    if (!isChunkLoadError(error) || reloadedRecently()) return;

    try {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      return;
    }
    window.location.reload();
  }

  override render() {
    return this.state.failed ? <RouteError /> : this.props.children;
  }
}

/** Envolve o `<Outlet />`: uma espera e uma queda para todas as rotas. */
export function RouteBoundaryWithSuspense({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  return (
    // ⚠️ **A `key` por rota não é cosmética — sem ela o fallback nunca aparece.**
    //
    // O React Router navega dentro de uma transição, e a regra do React para
    // transições é preservar a tela que já está montada até a próxima ficar
    // pronta. Num `Suspense` reaproveitado entre rotas isso vira o pior dos
    // mundos: medido aqui com o chunk atrasado em 2 s, a URL virava
    // `/privacidade` NA HORA e o miolo continuava mostrando a home pelos dois
    // segundos inteiros. A barra de endereço dizendo uma coisa e a tela outra,
    // e nenhum sinal de que o toque foi registrado — que é precisamente a
    // sensação de travamento que se quer eliminar.
    //
    // Trocando a `key`, o limite é OUTRO a cada rota: não há tela anterior para
    // preservar, e o React mostra o fallback. Numa conexão boa nada disso se vê
    // — o chunk chega antes dos 250ms de atraso do `route-fallback`.
    //
    // ⚠️ **`pathname`, e não `location.key` nem a URL com busca.** As abas da
    // pelada vivem na query (`?aba=caixa`), e o placar e o elenco também levam
    // estado por ali. Qualquer chave que inclua a busca remontaria a tela
    // inteira a cada troca de aba — o `useEffect` de carga rodaria de novo e a
    // aba custaria as três requisições do `load()`, transformando um clique
    // local numa ida à rede. O caminho é o que de fato identifica a TELA.
    <RouteBoundary key={pathname}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </RouteBoundary>
  );
}
